import { Download, ShieldCheck } from "lucide-react";
import { useT, useI18n } from "@/lib/i18n";

const BASE = "https://fritzkammag.ch/wp-content/uploads/2018/01";
const ISO_PDF = "/downloads/Zertifikat-ISO-9001-ISO-14001.pdf";
const ENAW_PDF = "/downloads/Zertifikat-ENAW.pdf";
const SSO_PDF = "/downloads/Charta-der-SSO.pdf";

const COPY = {
  de: {
    intro:
      "Unser Unternehmen arbeitet nach Swiss Safety Center zertifizierten Vorgaben — für jede Charge, in jedem Verfahren.",
    view_pdf: "PDF ansehen",
    items: [
      { id: "swiss-quality", image: `${BASE}/untitled-3-600x600-54-300x300.png`, title: "Swiss Quality", sub: "Garantiert" },
      { id: "co2",           image: `${BASE}/dsdcsdc-600x600-66-300x300.png`,     title: "CO₂-Emissionen", sub: "Reduziert" },
      { id: "iso-9001",      image: `${BASE}/SSC_ISO9001-300x300.jpg`,           title: "Swiss Safety Center", sub: "ISO 9001", pdf: ISO_PDF },
      { id: "iso-14001",     image: `${BASE}/SSC_ISO9001_ISO14001-300x300.jpg`,  title: "Swiss Safety Center", sub: "ISO 14001", pdf: ISO_PDF },
      { id: "enaw",          title: "ENAW", sub: "Energie-Zielvereinbarung", pdf: ENAW_PDF },
      { id: "sso",           title: "Charta der SSO", sub: "Swiss Surface Trade", pdf: SSO_PDF },
    ],
  },
  en: {
    intro:
      "Our company operates to Swiss Safety Center certified standards — for every batch, in every process.",
    view_pdf: "View PDF",
    items: [
      { id: "swiss-quality", image: `${BASE}/untitled-3-600x600-54-300x300.png`, title: "Swiss Quality", sub: "Guaranteed" },
      { id: "co2",           image: `${BASE}/dsdcsdc-600x600-66-300x300.png`,     title: "CO₂ emissions", sub: "Reduced" },
      { id: "iso-9001",      image: `${BASE}/SSC_ISO9001-300x300.jpg`,           title: "Swiss Safety Center", sub: "ISO 9001", pdf: ISO_PDF },
      { id: "iso-14001",     image: `${BASE}/SSC_ISO9001_ISO14001-300x300.jpg`,  title: "Swiss Safety Center", sub: "ISO 14001", pdf: ISO_PDF },
      { id: "enaw",          title: "ENAW", sub: "Energy agreement", pdf: ENAW_PDF },
      { id: "sso",           title: "Charter of the SSO", sub: "Swiss Surface Trade", pdf: SSO_PDF },
    ],
  },
  fr: {
    intro:
      "Notre entreprise travaille selon les standards certifiés Swiss Safety Center — pour chaque lot, dans chaque procédé.",
    view_pdf: "Voir le PDF",
    items: [
      { id: "swiss-quality", image: `${BASE}/untitled-3-600x600-54-300x300.png`, title: "Swiss Quality", sub: "Garantie" },
      { id: "co2",           image: `${BASE}/dsdcsdc-600x600-66-300x300.png`,     title: "Émissions CO₂", sub: "Réduites" },
      { id: "iso-9001",      image: `${BASE}/SSC_ISO9001-300x300.jpg`,           title: "Swiss Safety Center", sub: "ISO 9001", pdf: ISO_PDF },
      { id: "iso-14001",     image: `${BASE}/SSC_ISO9001_ISO14001-300x300.jpg`,  title: "Swiss Safety Center", sub: "ISO 14001", pdf: ISO_PDF },
      { id: "enaw",          title: "ENAW", sub: "Convention énergétique", pdf: ENAW_PDF },
      { id: "sso",           title: "Charte de la SSO", sub: "Swiss Surface Trade", pdf: SSO_PDF },
    ],
  },
  it: {
    intro:
      "La nostra azienda lavora secondo gli standard certificati Swiss Safety Center — per ogni lotto, in ogni procedimento.",
    view_pdf: "Vedi PDF",
    items: [
      { id: "swiss-quality", image: `${BASE}/untitled-3-600x600-54-300x300.png`, title: "Swiss Quality", sub: "Garantita" },
      { id: "co2",           image: `${BASE}/dsdcsdc-600x600-66-300x300.png`,     title: "Emissioni CO₂", sub: "Ridotte" },
      { id: "iso-9001",      image: `${BASE}/SSC_ISO9001-300x300.jpg`,           title: "Swiss Safety Center", sub: "ISO 9001", pdf: ISO_PDF },
      { id: "iso-14001",     image: `${BASE}/SSC_ISO9001_ISO14001-300x300.jpg`,  title: "Swiss Safety Center", sub: "ISO 14001", pdf: ISO_PDF },
      { id: "enaw",          title: "ENAW", sub: "Accordo energetico", pdf: ENAW_PDF },
      { id: "sso",           title: "Carta della SSO", sub: "Swiss Surface Trade", pdf: SSO_PDF },
    ],
  },
};

// Cert tile — as an <a> when a PDF is provided, else a static div.
function CertTile({ cert, i, viewLabel }) {
  const hasBadge = Boolean(cert.image);
  const hasPdf = Boolean(cert.pdf);
  const inner = (
    <>
      <div className="relative w-24 h-24 md:w-28 md:h-28 mb-6 flex items-center justify-center">
        {hasBadge ? (
          <div className="w-full h-full bg-white/95 p-2 ring-1 ring-white/10 group-hover:ring-white/30 transition-colors">
            <img
              src={cert.image}
              alt={`${cert.title} ${cert.sub}`}
              loading="lazy"
              className="w-full h-full object-contain"
            />
          </div>
        ) : (
          <div className="w-full h-full bg-white/[0.04] border border-white/10 group-hover:border-white/25 flex items-center justify-center transition-colors">
            <ShieldCheck className="w-10 h-10 text-white/70 group-hover:text-white transition-colors" strokeWidth={1.4} />
          </div>
        )}
      </div>
      <div className="font-display text-lg md:text-xl text-white tracking-tight leading-tight">
        {cert.title}
      </div>
      <div className="overline mt-2">{cert.sub}</div>
      {hasPdf && (
        <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-zinc-500 group-hover:text-white transition-colors">
          <Download className="w-3 h-3" />
          {viewLabel}
        </div>
      )}
    </>
  );

  const cls = `reveal reveal-delay-${(i % 4) + 1} group bg-[#0B0B0D] hover:bg-[#141416] transition-colors duration-500 p-8 md:p-10 flex flex-col items-start`;

  return hasPdf ? (
    <a
      href={cert.pdf}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`cert-${cert.id}`}
      className={cls}
    >
      {inner}
    </a>
  ) : (
    <div data-testid={`cert-${cert.id}`} className={cls}>
      {inner}
    </div>
  );
}

export const Certificates = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  return (
    <section
      id="zertifikate"
      data-testid="certificates-section"
      className="relative bg-[#0B0B0D] py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="overline reveal mb-4">{t("sections.zertifikate_overline", "— Zertifikate")}</div>
            <h2 className="reveal reveal-delay-1 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1] text-white max-w-2xl">
              {t("sections.zertifikate_title_1", "Geprüft. Dokumentiert.")}
              <br />
              <span className="text-zinc-500">{t("sections.zertifikate_title_2", "Schweizer Standard.")}</span>
            </h2>
          </div>
          <p className="reveal reveal-delay-2 text-zinc-400 max-w-md">
            {c.intro}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-white/10 border border-white/10">
          {c.items.map((cert, i) => (
            <CertTile key={cert.id} cert={cert} i={i} viewLabel={c.view_pdf} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Certificates;
